import { useState, useEffect, useRef } from 'react';
import type { FutebolEventoPartida, FutebolUltimoEvento } from '@/shared/types';
import { chaveGolDedup } from '@/features/futebol';

// Bolas decorativas nos cantos — dados estáticos fora do render
const BOLAS = [
  { x: 2,  y: 12, size: 32, delay: 0    },
  { x: 92, y: 10, size: 36, delay: 700  },
  { x: 4,  y: 80, size: 28, delay: 1400 },
  { x: 93, y: 78, size: 32, delay: 350  },
];

const DURACAO_GOL_MS = 7_000;

interface Props {
  hideBadge?:       boolean;
  evento?:          FutebolEventoPartida;
  placarCasa?:      number;
  placarVisitante?: number;
  initialKey?:      string | null;
}

export default function TvCopaTakeover({
  hideBadge = false, evento, placarCasa = 0, placarVisitante = 0, initialKey,
}: Props) {
  const [golAtivo, setGolAtivo] = useState(false);
  const prevKeyRef = useRef<string | null>(initialKey ?? null);

  const isGol  = evento != null && 'minuto' in evento && evento.tipo === 'gol';
  const golKey = isGol
    ? chaveGolDedup(
        (evento as FutebolUltimoEvento).time,
        placarCasa,
        placarVisitante,
      )
    : null;

  useEffect(() => {
    if (!golKey || golKey === prevKeyRef.current) return;
    prevKeyRef.current = golKey;
    setGolAtivo(true);
    const t = setTimeout(() => setGolAtivo(false), DURACAO_GOL_MS);
    return () => clearTimeout(t);
  }, [golKey]);

  return (
    <div className="fixed inset-0 z-[14] pointer-events-none overflow-hidden">

      {/* Vinheta Brasil — verde esquerda, amarelo direita */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            'radial-gradient(ellipse at 0% 50%, rgba(0,156,59,0.18) 0%, transparent 40%)',
            'radial-gradient(ellipse at 100% 50%, rgba(255,223,0,0.15) 0%, transparent 40%)',
            'radial-gradient(ellipse at 50% 0%, rgba(0,100,40,0.14) 0%, transparent 35%)',
          ].join(', '),
        }}
      />

      {/* Borda superior — verde → amarelo → verde */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(to right, #009c3b, #FFDF00 50%, #009c3b)', opacity: 0.75 }}
      />
      {/* Borda inferior */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(to right, #009c3b, #FFDF00 50%, #009c3b)', opacity: 0.75 }}
      />

      {/* Pulso de gol — borda verde brilhante por 7s */}
      {golAtivo && (
        <div
          className="absolute inset-0 animate-pulse-slow"
          style={{
            boxShadow: 'inset 0 0 0 5px rgba(0,156,59,0.90), inset 0 0 50px rgba(0,156,59,0.30)',
          }}
        />
      )}

      {/* Bolas de futebol decorativas nos cantos */}
      {BOLAS.map((b, i) => (
        <div
          key={i}
          className="absolute select-none animate-pulse-slow"
          style={{
            left:            `${b.x}%`,
            top:             `${b.y}%`,
            fontSize:        `${b.size}px`,
            opacity:         0.07,
            animationDelay:  `${b.delay}ms`,
          }}
        >
          ⚽
        </div>
      ))}

      {!hideBadge && (
        <div
          className="absolute bottom-6 right-6 flex items-center gap-1.5 rounded-full px-3 py-1.5 backdrop-blur-sm"
          style={{
            background: 'rgba(0,60,25,0.72)',
            border:     '1px solid rgba(255,223,0,0.40)',
          }}
        >
          <span className="text-xs font-bold tracking-wide" style={{ color: '#FFDF00' }}>
            🏆 Vamos Brasil!
          </span>
        </div>
      )}

    </div>
  );
}
